import React, { ReactNode, useState } from 'react';
import type { ReactSDK, EnvironmentState } from '@wix/dashboard-react';
import type { WixClient } from '@wix/sdk-react';
import {
  WixPatternsEssentials,
  createAppSettingsCapsule,
  getWixPatternsWindowExperiments,
  getErrorMonitorCompat,
  getExperimentsCompat,
  showToastCompat,
} from '@wix/bex-core';
import { Trans, useTranslation } from '@wix/bex-utils/@wix/wix-i18n-config';
import { CairoProvider as CairoProviderBase } from '../providers';
import { Essentials } from '@wix/fe-essentials/core';

export interface CairoProviderProps {
  children?: ReactNode;
}

interface CairoProviderPrivateProps extends CairoProviderProps {
  dashboard: Pick<ReactSDK, 'showToast' | 'navigate'>;
  client: Pick<WixClient, 'use'>;
  environment: EnvironmentState;
  essentials: Essentials;
}

export function WixPatternsDashboardBaseProvider({
  children,
  dashboard,
  environment: { locale, pageLocation },
  client,
  essentials,
  ...rest
}: CairoProviderPrivateProps) {
  const [value] = useState((): WixPatternsEssentials => {
    const environment = {
      language: locale || 'en',
      artifactId: essentials.environment.artifactId,
      componentId: pageLocation.pageId,
      providerType: 'bm' as const,
    };

    return {
      useExperiments: () => ({
        ready: true,
        experiments: getExperimentsCompat(essentials),
      }),
      experiments: getExperimentsCompat(essentials),
      Trans,
      useTranslation,
      user: {
        permissions: [],
      },
      showToast: showToastCompat(dashboard.showToast),
      navigateTo: ({ pageId }) => dashboard.navigate(pageId),
      onSidePanelOpen: undefined, // missing in dashboard-sdk
      onBeforeUnload: undefined, // missing in dashboard-sdk
      closeSidePanel: undefined, // missing in dashboard-sdk
      getHostContainer: undefined, // missing in dashboard-sdk
      environment,
      httpClient: essentials.httpClient,
      errorMonitor: getErrorMonitorCompat(essentials.asPartial),
      createEssentials: (options) => {
        return essentials.createChildEssentials({
          ...options,
          experiments:
            options.experiments &&
            'scopes' in options.experiments &&
            options.experiments?.scopes[0] === 'cairo'
              ? {
                  bag: getWixPatternsWindowExperiments() ?? {},
                }
              : options.experiments,
        });
      },
      createComponentSettings: createAppSettingsCapsule,
    };
  });

  return (
    <CairoProviderBase {...rest} value={value}>
      {children}
    </CairoProviderBase>
  );
}
